import { Drawer } from 'components/Drawer'
import { useRef, useState } from 'react'
import Styles from './ImagePickerDrawer.module.scss'
import cx from 'classnames'
import { ImagePickerModalContainerProps, Providers } from 'components/ImagePickerModal/Types'
import { ImagePickerProvider, useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { Stock } from 'components/ImagePickerModal/Stock/Stock'
import { Gifs } from 'components/ImagePickerModal/Gifs/Gifs'
import { Link } from 'components/ImagePickerModal/Link/Link'
import { DefaultPanel } from './DefaultPanel/DefaultPanel'
import { Title } from './Title'
import { Uploads } from './Uploads/Uploads'
import { UploadFromDevice } from './UploadFromDevice/UploadFromDevice'
import { ImagePickerDrawerProps, ImagePickerInnerContentProps, ImagePickerTopProps } from './Types'
import { AiGeneration } from '../AIGeneration/AiGeneration'
import { ButtonVariant } from 'blaze-ui/Button/Types'
import { BrandKits } from 'components/ImagePickerModal/ImagePickerDrawer/BrandKits/BrandKits'

const ImagePickerDrawerContainer = (props: ImagePickerModalContainerProps & ImagePickerDrawerProps) => (
  <ImagePickerProvider {...props}>
    <ImagePickerDrawer {...props} />
  </ImagePickerProvider>
)

export const ImagePickerDrawerTitle = ({ currentPanel }: ImagePickerTopProps) => {
  const { provider } = useImagePickerContext()
  const isUploadingFromDevice = currentPanel === 'uploadFromDevice'

  return (
    <Drawer.Title asChild>
      <Title title={isUploadingFromDevice ? 'Upload' : undefined} provider={provider} />
    </Drawer.Title>
  )
}

export const ImagePickerDrawerBack = ({
  currentPanel,
  setCurrentPanel,
  hiddenProviders,
  variant,
  label,
}: ImagePickerTopProps & { variant?: ButtonVariant; label?: string }) => {
  const { setSelectedImages, provider, setProvider } = useImagePickerContext()
  const singleProvider = hiddenProviders && Object.values(Providers).length - hiddenProviders?.size === 1

  const handleClose = () => {
    setCurrentPanel?.(undefined)
    setProvider(undefined)
    setSelectedImages([])
  }

  if (singleProvider || (!currentPanel && !provider)) return <div />

  return (
    <Drawer.Back onPress={handleClose} variant={variant}>
      {label}
    </Drawer.Back>
  )
}

export const ImagePickerDrawerTopContent = ({
  currentPanel,
  hiddenProviders,
  setCurrentPanel,
}: ImagePickerTopProps) => {
  const props = {
    currentPanel,
    setCurrentPanel,
    hiddenProviders,
  }

  return (
    <Drawer.Top>
      <ImagePickerDrawerBack {...props} variant="ghost" />
      <ImagePickerDrawerTitle {...props} />
      <Drawer.Close variant="ghost" position="right" />
    </Drawer.Top>
  )
}

export const ImagePickerDrawerContent = (props: ImagePickerInnerContentProps) => {
  const { hiddenProviders, onUploadStart, currentPanel, setCurrentPanel, confirmLabel, brandKitId, insertToEditor } =
    props
  const { provider, selectedImages } = useImagePickerContext()
  const actionsContainerRef = useRef<HTMLDivElement>(null)

  return (
    <Drawer.Container padding={!provider && !currentPanel ? '15px 0 0 0' : undefined}>
      {!provider && !currentPanel && (
        <DefaultPanel
          setCurrentPanel={setCurrentPanel}
          hiddenProviders={hiddenProviders}
          insertToEditor={insertToEditor}
        />
      )}
      {!provider && currentPanel === 'uploadFromDevice' && (
        <UploadFromDevice actionsContainerRef={actionsContainerRef} onUploadStart={onUploadStart} />
      )}
      {(provider || currentPanel) && (
        <div
          className={cx(Styles.container, {
            [Styles.selectedImages]: selectedImages.length > 0,
          })}
        >
          {provider === 'ai' && <AiGeneration actionsContainerRef={actionsContainerRef} />}
          {provider === 'stock' && <Stock actionsContainerRef={actionsContainerRef} confirmLabel={confirmLabel} />}
          {provider === 'uploads' && <Uploads actionsContainerRef={actionsContainerRef} confirmLabel={confirmLabel} />}
          {provider === 'brandKits' && (
            <BrandKits
              actionsContainerRef={actionsContainerRef}
              confirmLabel={confirmLabel}
              initialBrandKitId={brandKitId}
            />
          )}
          {provider === 'gifs' && <Gifs actionsContainerRef={actionsContainerRef} />}
          {provider === 'link' && <Link actionsContainerRef={actionsContainerRef} confirmLabel={confirmLabel} />}
          <div className={Styles.uploadSelected} ref={actionsContainerRef} />
        </div>
      )}
    </Drawer.Container>
  )
}

const ImagePickerDrawer = (props: ImagePickerModalContainerProps & ImagePickerDrawerProps) => {
  const [currentPanel, setCurrentPanel] = useState<string | undefined>(undefined)
  const imagePickerProps = { ...props, currentPanel, setCurrentPanel }

  return (
    <Drawer.Root {...props}>
      <Drawer.Portal>
        <Drawer.Content fullHeight color="var(--light-40)">
          <Drawer.InnerContent isSnappedTop>
            <ImagePickerDrawerTopContent {...imagePickerProps} />
            <ImagePickerDrawerContent {...imagePickerProps} />
          </Drawer.InnerContent>
        </Drawer.Content>
        <Drawer.Overlay />
      </Drawer.Portal>
    </Drawer.Root>
  )
}

export { ImagePickerDrawerContainer as ImagePickerDrawer }
